import React from 'react';
import ReactDOM from 'react-dom';
import { observer } from 'mobx-react';
import { observable, computed , toJS } from 'mobx';
import cx from 'classnames';
import { isEmpty, isFunction, filter, find } from 'lodash';
import { isBlank, isPresent } from '@seedlang/utils';
import styled from '@emotion/styled';
import { Theme } from '@seedlang/constants';
import Spinner from './spinner';

const Wrapper = styled.div`
  position: relative;
  border: ${props => props.border};
`;

const SearchResults = styled.ul`
  position: ${props => props.portal ? 'fixed' : 'absolute'};
  top: ${props => props.portal ? `${props.top}px` : '42px'};
  left: ${props => props.portal ? `${props.left}px` : '0'};
  background: white;
  width: ${props => props.portal ? `${props.width}px` : '300px'};
  border: 1px solid #bdbdbd;
  z-index: ${props => props.portal ? Theme.z['topmost'] : Theme.z['foreground']};
  li {
    padding: 3px 10px;
    font-size: 14px;
    cursor: pointer;
    margin: 0;
  }
  li.selected {
    background: whitesmoke;
  }
  li:not(:last-child) {
    border-bottom: 1px solid #bdbdbd;
  }
  li:hover {
    background: whitesmoke;
  }
`;

const ESCAPE_KEYCODE = 27;
const ENTER_KEYCODE = 13;
const DOWN_KEYCODE = 40;
const UP_KEYCODE = 38;

@observer
class TypeAhead extends React.Component {
  constructor(props) {
    super(props);
    this.inputRef = React.createRef();
  }

  @observable position = -1;
  @observable selectedOptions = [];
  @observable lastTextEntry;
  @observable _mounted = true;
  @observable focused = false;
  @observable searchResultsRect = null;
  searchResultsListenersAdded = false;

  static defaultProps = {
    border: '1px solid #bdbdbd',
  };

  componentDidMount() {
    this.updateSearchResultsListeners();
    if (this.props.defaultValue) {
      this.onFocus();
      this.inputRef.current.value = this.props.defaultValue;
      this.lastTextEntry = this.props.defaultValue;
      this.performSearch();
    }
  }

  @computed get hasOptions() {
    return isPresent(toJS(this.options));
  }

  @computed get options() {
    if (this.props.localFilter) {
      return this.selectedOptions;
    }
    return this.props.options;
  }

  componentDidUpdate(prevProps) {
    if (prevProps.searchResultsInPortal !== this.props.searchResultsInPortal) {
      this.updateSearchResultsListeners();
    }
  }

  componentWillUnmount() {
    this._mounted = false;
    this.removeSearchResultsListeners();
  }
  onFocus = () => {
    if (this.props.onFocus) {
      this.props.onFocus();
    }
    this.focused = true;
    this.updateSearchResultsPosition();
  }
  onBlur = () => {
    if (this.props.onBlur) {
      this.props.onBlur();
    }
    if (this.props.enterOnTab) {
      this.onSelectCurrent();
    } else if (!this.props.keepInputOnBlur) {
      this.interval = setInterval(this.resetInput, 1100);
    }
  }
  onKeyUp = (e) => {
    if (e.keyCode === ESCAPE_KEYCODE) {
      this.props.onClear();
    } else if (e.keyCode === ENTER_KEYCODE) {
      this.onSelectCurrent();
    } else if (e.keyCode === DOWN_KEYCODE) {
      this.onIncrementPosition();
    } else if (e.keyCode === UP_KEYCODE) {
      this.onDecrementPosition();
    } else if (this.props.localFilter) {
      this.filter(this.inputRef.current.value);
    } else {
      this.lastTextEntry = this.inputRef.current.value;
      clearInterval(this.interval);
      this.interval = setTimeout(this.performSearch, 100);
    }
  }
  performSearch = () => {
    this.props.onKeyUp(this.lastTextEntry);
  }
  onSelectCurrent = () => {
    if (this.props.onCreateValue && isFunction(this.props.onCreateValue) && (this.position === -1 || isEmpty(this.options))) {
      this.props.onCreateValue(this.inputRef.current.value);
    } else {
      const options = toJS(this.options);
      const index = this.position === -1 ? 0 : this.position;
      const selected = options[index];
      if (selected) {
        this.props.onSelect(selected);
      }
    }
  }
  onSelect = (id) => {
    const result = find(toJS(this.options), { id: id });
    this.props.onSelect(result);
    if (!this.props.keepInputOnSelect) {
      this.resetInput();
    }
  }
  onIncrementPosition = () => {
    if (this.position < this.options.length - 1) {
      this.position = this.position + 1;
    }
  }
  onDecrementPosition = () => {
    if (this.position >= 0) {
      this.position = this.position - 1;
    }
  }
  resetInput = () => {
    if (this._mounted) {
      this.focused = false;
      clearInterval(this.interval);
      this.position = -1;
      this.selectedOptions = [];
      this.searchResultsRect = null;
      this.inputRef.current.value = '';
      if (this.props.onClear) {
        this.props.onClear();
      }
    }
  }

  filter(value) {
    if (isBlank(value)) {
      this.selectedOptions = [];
    } else {
      this.selectedOptions = filter(this.props.options, item => item[this.props.displayField] && item[this.props.displayField].match(value));
    }
  }

  searchResultsPortalProps = () => {
    if (!this.props.searchResultsInPortal || !this.inputRef.current?.getBoundingClientRect) {
      return {};
    }
    const rect = this.searchResultsRect || this.inputRef.current.getBoundingClientRect();
    return {
      portal: true,
      top: this.searchResultsRect ? rect.top : rect.bottom + 5,
      left: rect.left,
      width: rect.width,
    };
  }

  updateSearchResultsPosition = () => {
    if (!this.props.searchResultsInPortal || !this.focused || !this.inputRef.current?.getBoundingClientRect) {
      return;
    }
    const rect = this.inputRef.current.getBoundingClientRect();
    this.searchResultsRect = {
      top: rect.bottom + 5,
      left: rect.left,
      width: rect.width,
    };
  }

  updateSearchResultsListeners = () => {
    if (this.props.searchResultsInPortal && !this.searchResultsListenersAdded) {
      window.addEventListener('scroll', this.updateSearchResultsPosition, true);
      window.addEventListener('resize', this.updateSearchResultsPosition);
      this.searchResultsListenersAdded = true;
    } else if (!this.props.searchResultsInPortal) {
      this.removeSearchResultsListeners();
    }
  }

  removeSearchResultsListeners = () => {
    if (this.searchResultsListenersAdded) {
      window.removeEventListener('scroll', this.updateSearchResultsPosition, true);
      window.removeEventListener('resize', this.updateSearchResultsPosition);
      this.searchResultsListenersAdded = false;
    }
  }

  isExcluded = (id) => {
    return this.props.excludeIds && this.props.excludeIds.map(String).includes(String(id));
  }

  renderSearchResults = () => {
    if (this.focused && this.hasOptions) {
      return (
        <SearchResults {...this.searchResultsPortalProps()}>
          {
            this.options.map((item, index) => {
              if (!this.isExcluded(item.id)) {
                return (
                  <li
                    key={index}
                    className={cx({ selected: index === this.position })}
                    onClick={this.onSelect.bind(this, item.id)}
                  >
                    {this.props.renderFunction
                      ? this.props.renderFunction(item)
                      : (
                        <>
                          {
                            isFunction(item[this.props.displayField]) && item[this.props.displayField]()
                          }
                          {
                            !isFunction(item[this.props.displayField]) && item[this.props.displayField]
                          }
                        </>
                      )
                    }
                  </li>
                );
              } else {
                return null;
              }
            })
          }
        </SearchResults>
      );
    }
    if (this.focused && this.props.loading) {
      return (
        <SearchResults {...this.searchResultsPortalProps()}>
          <Spinner />
        </SearchResults>
      );
    }
    return null;
  }

  render() {

    const Component = this.props.component || 'input';
    const searchResults = this.renderSearchResults();
    return (
      <Wrapper>
        <Component
          ref={this.inputRef}
          type='text'
          onKeyUp={this.onKeyUp}
          placeholder={this.props.placeholder}
          onBlur={this.onBlur}
          onFocus={this.onFocus}
          height={this.props.height}
          style={{border: this.props.border}}
        />
        {
          !this.props.searchResultsInPortal && searchResults
        }
        {
          this.props.searchResultsInPortal && searchResults && ReactDOM.createPortal(searchResults, document.body)
        }
      </Wrapper>
    );
  }
}

export default TypeAhead;
